/**
 * Rich text editor for BSEE — markup-driven. Todos, notes and docs save plain Markdown; an HTML
 * mode for newsletters and messages is planned (the stored format belongs to the surface).
 *
 *   <textarea name="notes" data-bsee-editor
 *             data-bsee-mentions='[{"token":"Sue","name":"Sue Nelson","initials":"SN"}]'></textarea>
 *
 * The textarea stays in the form (hidden) and always holds the Markdown, so the server, the
 * database and every existing reader are unchanged. The editor shows that Markdown formatted
 * as you type: bold, lists, clickable checkboxes, and @mentions as chips.
 *
 * Mentions: typing `@` or `@@` opens a pick list at the caret; `@@` marks a done-notify
 * mention. The saved text is plain `@Sue` / `@@Sue` — exactly what Mentions.php parses — so what
 * a mention MEANS (notify / done-notify) is still decided on the server.
 *
 * WHY TIPTAP: headless (no UI of its own, so BSEE styles it), MIT, built on ProseMirror, which
 * has already solved caret/paste/undo/IME. Only the MIT core packages are used.
 *
 * Fields present at load, and fields added later (AJAX, a re-rendered form), are both picked up.
 *
 * @see https://tiptap.dev
 */

import { Editor, type JSONContent, type MarkdownToken } from '@tiptap/core';
import { Markdown } from '@tiptap/markdown';
import Mention from '@tiptap/extension-mention';
import BubbleMenu from '@tiptap/extension-bubble-menu';
import FloatingMenu from '@tiptap/extension-floating-menu';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import Strike from '@tiptap/extension-strike';
import StarterKit from '@tiptap/starter-kit';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import { TaskItem } from '@tiptap/extension-task-item';
import { TaskList } from '@tiptap/extension-task-list';

export interface MentionPerson {
    token: string;
    name: string;
    initials: string;
}

const SELECTOR = 'textarea[data-bsee-editor]';

// The editor behind each textarea, so page scripts can set its content (see setEditorMarkdown).
const editors = new WeakMap<HTMLTextAreaElement, Editor>();
const ATTACHED = 'bseeEditorAttached';
const MENTION_TOKEN = 'bseeMention';
const MAX_PICK_LIST = 8;

const readPeople = (field: HTMLElement): MentionPerson[] => {
    try {
        return JSON.parse(field.dataset.bseeMentions ?? '[]') as MentionPerson[];
    } catch {
        return [];
    }
};

// "Starts with", not fuzzy: "ki" must not find "Alyzza Perkins".
const matches = (person: MentionPerson, typed: string): boolean => {
    const needle = typed.toLowerCase();
    return person.token.toLowerCase().startsWith(needle)
        || person.name.toLowerCase().startsWith(needle)
        || person.initials.toLowerCase().startsWith(needle);
};

// ═══════════════════════════════════════════════════════════════════════════════
// Mention node — plain `@Sue` / `@@Sue` in Markdown, a chip in the editor
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Only names in `people` become chips, so an email address or `@2x` stays plain text.
 * Same pattern as Mentions.php: not preceded by a word character or another @.
 */
export const mentionExtension = (people: MentionPerson[]) => {
    const knownTokens = new Map<string, string>();
    people.forEach(person => knownTokens.set(person.token.toLowerCase(), person.token));

    return Mention.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                double: {
                    default: false,
                    parseHTML: (element: HTMLElement) => element.dataset.double === '1',
                    renderHTML: (attributes: Record<string, unknown>) => ({ 'data-double': attributes.double ? '1' : '0' }),
                },
            };
        },

        markdownTokenName: MENTION_TOKEN,

        markdownTokenizer: {
            name: MENTION_TOKEN,
            level: 'inline',
            start: (src: string) => src.search(/(?<![\w@])@/),
            tokenize: (src: string): MarkdownToken | undefined => {
                const found = /^(@@?)([A-Za-z0-9]+)/.exec(src);
                if (found === null) {
                    return undefined;
                }
                const token = knownTokens.get(found[2].toLowerCase());
                if (token === undefined) {
                    return undefined;
                }
                return { type: MENTION_TOKEN, raw: found[0], token, double: found[1] === '@@' };
            },
        },

        parseMarkdown: (token: MarkdownToken) => ({
            type: 'mention',
            attrs: { id: token.token, label: token.token, double: token.double === true },
        }),

        renderMarkdown: (node: JSONContent) => {
            const marker = node.attrs?.double === true ? '@@' : '@';
            return `${marker}${node.attrs?.label}`;
        },
    });
};

// ═══════════════════════════════════════════════════════════════════════════════
// Link — bare URLs and emails stay bare
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * The Markdown parser turns a bare `bob@example.com` or URL into a link; the default
 * serializer would then save it back as `[bob@example.com](mailto:bob@example.com)`, rewriting
 * text nobody touched. A link whose text is its own address is saved bare.
 */
const BareLink = Link.extend({
    // For a mark the serializer asks twice with a placeholder child to learn the opening and
    // closing syntax; the real link text arrives as `context.meta.markText`.
    renderMarkdown: (node, helpers, context) => {
        const href: string = node.attrs?.href ?? '';
        const title: string = node.attrs?.title ?? '';
        const text = helpers.renderChildren(node);
        const linkText: string = context.meta?.markText ?? '';
        // Bare only when the serializer won't escape anything in the text: it writes `_`, `&`, `*`
        // and friends as `\_` / `&amp;`, which an autolink does NOT read back — the link would
        // end early or point somewhere else. Those go out as a full [text](href) link instead.
        const plainText = /^[A-Za-z0-9.\/:?=%+,;@#!$'()-]+$/.test(linkText);
        if (plainText && title === '' && (linkText === href || `mailto:${linkText}` === href)) {
            return text;
        }
        return title === '' ? `[${text}](${href})` : `[${text}](${href} "${title}")`;
    },
});

/**
 * Tiptap's own matcher excludes the trigger character from the query, so it can't see `@@`.
 * This one reads the text before the caret: `@` or `@@`, then the letters typed so far. The
 * query keeps a leading `@` for the double form, which the pick list and the command read.
 */
export const findMentionMatch = ({ $position }: { $position: { pos: number; nodeBefore: { text?: string | null } | null } }) => {
    const before = $position.nodeBefore?.text ?? '';
    const found = /(?:^|\s)(@@?)([A-Za-z0-9]*)$/.exec(before);
    if (found === null) {
        return null;
    }
    const typed = found[0].trimStart();
    const to = $position.pos;
    return {
        range: { from: to - typed.length, to },
        query: (found[1] === '@@' ? '@' : '') + found[2],
        text: typed,
    };
};

// ═══════════════════════════════════════════════════════════════════════════════
// Pick list popup — positioned at the caret, BS5 dropdown look
// ═══════════════════════════════════════════════════════════════════════════════

interface PickProps {
    items: MentionPerson[];
    query: string;
    command: (person: { id: string; label: string }) => void;
    clientRect?: (() => DOMRect | null) | null;
}

const pickList = () => {
    const menu = document.createElement('div');
    menu.className = 'dropdown-menu show bsee-mention-list';
    let props: PickProps;
    let selected = 0;

    const draw = (): void => {
        menu.replaceChildren();
        const hint = props.query.startsWith('@') ? 'notify + done' : 'notify';
        props.items.forEach((person, index) => {
            const row = document.createElement('button');
            row.type = 'button';
            row.className = 'dropdown-item' + (index === selected ? ' active' : '');
            const initials = person.initials === '' ? '' : ` (${person.initials})`;
            row.textContent = `${person.name}${initials} `;
            const note = document.createElement('small');
            note.className = 'text-body-secondary';
            note.textContent = hint;
            row.append(note);
            row.addEventListener('mousedown', event => {
                event.preventDefault();
                choose(index);
            });
            menu.append(row);
        });
        const rect = props.clientRect?.() ?? null;
        if (rect !== null) {
            menu.style.left = `${rect.left}px`;
            menu.style.top = `${rect.bottom + 4}px`;
        }
        menu.hidden = props.items.length === 0;
    };

    const choose = (index: number): void => {
        const person = props.items[index];
        if (person !== undefined) {
            props.command({ id: person.token, label: person.token });
        }
    };

    return {
        onStart: (startProps: PickProps): void => {
            props = startProps;
            selected = 0;
            document.body.append(menu);
            draw();
        },
        onUpdate: (updateProps: PickProps): void => {
            props = updateProps;
            selected = 0;
            draw();
        },
        onKeyDown: ({ event }: { event: KeyboardEvent }): boolean => {
            if (props.items.length === 0) {
                return false;
            }
            if (event.key === 'ArrowDown') {
                selected = (selected + 1) % props.items.length;
                draw();
                return true;
            }
            if (event.key === 'ArrowUp') {
                selected = (selected + props.items.length - 1) % props.items.length;
                draw();
                return true;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
                choose(selected);
                return true;
            }
            return false;
        },
        onExit: (): void => {
            menu.remove();
        },
    };
};

// ═══════════════════════════════════════════════════════════════════════════════
// Editor
// ═══════════════════════════════════════════════════════════════════════════════

/** Everything except mentions; shared with the round-trip test. */
export const baseExtensions = ({ checklist = true }: { checklist?: boolean } = {}) => {
    const extensions = [
        // Strike wraps links (not the other way round): "[~~url~~](url)" is read back by the server's
        // autolinker as a broken link, "~~[url](url)~~" is not.
        StarterKit.configure({ link: false, strike: false, listItem: false }),
        Strike.extend({ priority: 1100 }),
        // Old docs have headings and code blocks as a bullet's own content ("- ## Intro"); the stock
        // list item needs a paragraph first and would push those out of the list.
        ListItem.extend({ content: 'block+' }),
        BareLink.configure({ openOnClick: false }),
        // GFM tables; without these an old doc's table is flattened to run-together paragraphs on save.
        Table,
        TableRow,
        TableHeader,
        TableCell,
        // Without it an <img> in an old doc is dropped on save.
        Image.configure({ inline: true }),
        Markdown.configure({ markedOptions: { breaks: true } }),
    ];
    if (checklist) {
        extensions.push(TaskList, TaskItem.configure({ nested: true }));
    }

    return extensions;
};

interface MenuItem {
    title: string;
    /** Font Awesome class for an icon button; leave empty to show `label` text instead. */
    icon: string;
    label: string;
    /** Node or mark name; the button shows pressed while it is active at the caret. */
    active: string;
    /** Heading level, so H1..H4 each show pressed only for their own level. */
    level?: number;
    /** The button asks for a link address in the menu itself instead of running at once. */
    asksAddress?: boolean;
    run: (editor: Editor) => void;
}

const headingItem = (level: 1 | 2 | 3 | 4): MenuItem => ({
    title: `Heading ${level}`,
    icon: '',
    label: `H${level}`,
    active: 'heading',
    level,
    run: editor => editor.chain().focus().toggleHeading({ level }).run(),
});

// Floating menu: on an empty line. Start a block, or start a mention.
const FLOATING_ITEMS: MenuItem[] = [
    headingItem(1),
    headingItem(2),
    headingItem(3),
    headingItem(4),
    { title: 'Divider', icon: 'fa-minus', label: '', active: '', run: editor => editor.chain().focus().setHorizontalRule().run() },
    { title: 'Bullet list', icon: 'fa-list-ul', label: '', active: 'bulletList', run: editor => editor.chain().focus().toggleBulletList().run() },
    { title: 'Numbered list', icon: 'fa-list-ol', label: '', active: 'orderedList', run: editor => editor.chain().focus().toggleOrderedList().run() },
    { title: 'Checklist', icon: 'fa-square-check', label: '', active: 'taskList', run: editor => editor.chain().focus().toggleTaskList().run() },
    { title: 'Code block', icon: 'fa-square-code', label: '', active: 'codeBlock', run: editor => editor.chain().focus().toggleCodeBlock().run() },
    { title: 'Notify someone', icon: '', label: '@', active: '', run: editor => editor.chain().focus().insertContent('@').run() },
    { title: 'Notify someone now and when done', icon: '', label: '@@', active: '', run: editor => editor.chain().focus().insertContent('@@').run() },
];

// Bubble menu: on selected text. Format it.
const BUBBLE_ITEMS: MenuItem[] = [
    headingItem(1),
    headingItem(2),
    headingItem(3),
    headingItem(4),
    { title: 'Bold', icon: 'fa-bold', label: '', active: 'bold', run: editor => editor.chain().focus().toggleBold().run() },
    { title: 'Italic', icon: 'fa-italic', label: '', active: 'italic', run: editor => editor.chain().focus().toggleItalic().run() },
    { title: 'Strikethrough', icon: 'fa-strikethrough', label: '', active: 'strike', run: editor => editor.chain().focus().toggleStrike().run() },
    { title: 'Code', icon: 'fa-code', label: '', active: 'code', run: editor => editor.chain().focus().toggleCode().run() },
    { title: 'Link', icon: 'fa-link', label: '', active: 'link', asksAddress: true, run: () => undefined },
];

function applyLink(editor: Editor, address: string): void {
    if (address.trim() === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: address.trim() }).run();
}

const buildMenu = (items: MenuItem[], editor: () => Editor): HTMLElement => {
    const bar = document.createElement('div');
    bar.className = 'btn-group btn-group-sm bsee-editor-menu';
    // Keep the caret in the editor when a button is pressed (a button takes focus on mousedown);
    // the link address field is the one thing in the bar that needs focus.
    bar.addEventListener('mousedown', event => {
        if (!(event.target instanceof HTMLInputElement)) {
            event.preventDefault();
        }
    });
    const buttons: HTMLButtonElement[] = [];
    const addressRow = document.createElement('div');
    addressRow.className = 'input-group input-group-sm d-none';
    const addressInput = document.createElement('input');
    addressInput.type = 'text';
    addressInput.className = 'form-control';
    addressInput.placeholder = 'Link address (empty removes it)';
    addressInput.setAttribute('aria-label', 'Link address');
    const addressOk = document.createElement('button');
    addressOk.type = 'button';
    addressOk.className = 'btn btn-primary';
    addressOk.title = 'Apply link';
    addressOk.innerHTML = '<i class="fa-solid fa-check"></i>';
    addressRow.append(addressInput, addressOk);
    const showAddress = (show: boolean): void => {
        addressRow.classList.toggle('d-none', !show);
        buttons.forEach(button => button.classList.toggle('d-none', show));
    };
    const finishAddress = (apply: boolean): void => {
        if (apply) {
            applyLink(editor(), addressInput.value);
        }
        showAddress(false);
        editor().chain().focus().run();
    };
    addressOk.addEventListener('click', () => finishAddress(true));
    addressInput.addEventListener('keydown', event => {
        if (event.key === 'Enter') {
            event.preventDefault();
            finishAddress(true);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            finishAddress(false);
        }
    });
    items.forEach(item => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn-secondary';
        button.title = item.title;
        button.dataset.active = item.active;
        if (item.level !== undefined) {
            button.dataset.level = String(item.level);
        }
        if (item.icon === '') {
            button.textContent = item.label;
        } else {
            const icon = document.createElement('i');
            icon.className = `fa-solid ${item.icon}`;
            button.append(icon);
        }
        button.addEventListener('click', () => {
            if (item.asksAddress) {
                addressInput.value = editor().getAttributes('link').href ?? '';
                showAddress(true);
                addressInput.focus();
                return;
            }
            item.run(editor());
        });
        buttons.push(button);
        bar.append(button);
    });
    bar.append(addressRow);
    return bar;
};

const showActive = (menu: HTMLElement, editor: Editor): void => {
    menu.querySelectorAll<HTMLButtonElement>('button').forEach(button => {
        const name = button.dataset.active ?? '';
        const level = button.dataset.level;
        const pressed = level === undefined ? editor.isActive(name) : editor.isActive(name, { level: Number(level) });
        button.classList.toggle('active', name !== '' && pressed);
    });
};

const attach = (field: HTMLTextAreaElement): void => {
    if (field.dataset[ATTACHED] === '1') {
        return;
    }
    field.dataset[ATTACHED] = '1';

    const people = readPeople(field);
    // data-bsee-no-checklist: a field whose saved text can't be edited later (a todo's notes are an
    // append-only log) would leave a checkbox nobody can tick, so it gets no checklist.
    const checklist = field.dataset.bseeNoChecklist === undefined;

    const wrapper = document.createElement('div');
    wrapper.className = 'bsee-editor';
    const body = document.createElement('div');
    body.className = 'form-control bsee-editor-body';
    body.style.height = field.style.height;
    body.style.minHeight = field.style.minHeight;
    const floatingItems = checklist ? FLOATING_ITEMS : FLOATING_ITEMS.filter(item => item.active !== 'taskList');
    const floatingMenu = buildMenu(floatingItems, () => editor);
    const bubbleMenu = buildMenu(BUBBLE_ITEMS, () => editor);
    wrapper.append(body, floatingMenu, bubbleMenu);
    field.after(wrapper);
    field.hidden = true;

    const editor: Editor = new Editor({
        element: body,
        // Existing text has single newlines between lines; `breaks` keeps them as line breaks.
        content: field.value,
        contentType: 'markdown',
        // Same look as the rendered doc (.doc-reader__html), so what you type is what the reader sees.
        editorProps: { attributes: { class: 'doc-reader__html' } },
        // data-bsee-autofocus: the body is the first thing typed into (e.g. adding a note to a todo).
        autofocus: field.dataset.bseeAutofocus === undefined ? false : 'end',
        extensions: [
            ...baseExtensions({ checklist }),
            FloatingMenu.configure({ element: floatingMenu, options: { placement: 'bottom-start', offset: 6 } }),
            BubbleMenu.configure({ element: bubbleMenu, options: { placement: 'top', offset: 6 } }),
            mentionExtension(people).configure({
                HTMLAttributes: { class: 'bsee-mention' },
                renderHTML: ({ node, options }) => [
                    'span',
                    {
                        ...options.HTMLAttributes,
                        'data-type': 'mention',
                        'data-id': node.attrs.id,
                        'data-label': node.attrs.label,
                        'data-double': node.attrs.double ? '1' : '0',
                    },
                    `${node.attrs.double ? '@@' : '@'}${node.attrs.label}`,
                ],
                suggestion: {
                    char: '@',
                    findSuggestionMatch: findMentionMatch,
                    items: ({ query }: { query: string }) => people
                        .filter(person => matches(person, query.replace(/^@/, '')))
                        .slice(0, MAX_PICK_LIST),
                    command: ({ editor: activeEditor, range, props }: { editor: Editor; range: { from: number; to: number }; props: { id: string; label: string } }) => {
                        const typed = activeEditor.state.doc.textBetween(range.from, range.to);
                        activeEditor.chain().focus().insertContentAt(range, [
                            { type: 'mention', attrs: { id: props.id, label: props.label, double: typed.startsWith('@@') } },
                            { type: 'text', text: ' ' },
                        ]).run();
                    },
                    render: pickList,
                },
            }),
        ],
        // Autofocus happens before the menus' plugins are listening, so the floating menu would
        // stay hidden on an empty, focused editor until the first click; an empty transaction wakes it.
        onCreate: () => {
            window.setTimeout(() => editor.view.dispatch(editor.state.tr), 0);
        },
        onUpdate: () => {
            field.value = editor.getMarkdown();
            field.dispatchEvent(new Event('input', { bubbles: true }));
        },
        onTransaction: () => {
            showActive(floatingMenu, editor);
            showActive(bubbleMenu, editor);
        },
    });
    editors.set(field, editor);
};

/**
 * The server's rendering of a doc (League CommonMark) → HTML Tiptap reads as the same document.
 *
 * WHY load HTML instead of the Markdown: the editor's own Markdown parser reads sloppy
 * indentation differently from the server's (a one-space "sub-item" becomes plain text), so
 * thousands of existing docs would look different the moment they opened. Starting from the
 * server's HTML shows exactly what the doc looks like today.
 *
 * Two things the server's HTML needs for the editor: checkbox lists as task lists, and mention
 * chips as mention nodes.
 */
export const htmlForEditor = (html: string): string => {
    const holder = document.createElement('div');
    holder.innerHTML = html;

    // Checkbox lists. A list can mix checkbox and plain items (a typo like "- [] x" among real
    // "- [x] y" items); a task list can only hold task items, so a mixed list is split into runs.
    holder.querySelectorAll('ul').forEach(list => {
        const items = Array.from(list.children).filter(child => child.tagName === 'LI');
        const hasBox = (item: Element): boolean => item.querySelector(':scope > input[type="checkbox"]') !== null;
        if (!items.some(hasBox)) {
            return;
        }

        let currentList: Element = list;
        let currentIsTask = hasBox(items[0]);
        items.forEach((item, index) => {
            const isTask = hasBox(item);
            if (index > 0 && isTask !== currentIsTask) {
                const nextList = document.createElement('ul');
                currentList.after(nextList);
                currentList = nextList;
                currentIsTask = isTask;
            }
            if (currentList !== list) {
                currentList.append(item);
            }
            if (isTask) {
                const box = item.querySelector(':scope > input[type="checkbox"]') as Element;
                item.setAttribute('data-type', 'taskItem');
                item.setAttribute('data-checked', box.hasAttribute('checked') ? 'true' : 'false');
                box.remove();
                currentList.setAttribute('data-type', 'taskList');
            }
        });
    });

    // "* * text" (a bullet whose whole content is another list): the editor's Markdown writer
    // can't express an empty bullet holding a list — it writes the sub-items out flat and loose.
    // The author meant one bullet, so the wrapper is dropped and its items join the parent list.
    holder.querySelectorAll('li').forEach(item => {
        const only = item.children.length === 1 ? item.firstElementChild : null;
        const isWrapper = only !== null && (only.tagName === 'UL' || only.tagName === 'OL')
            && (item.textContent ?? '').trim() === (only.textContent ?? '').trim();
        if (isWrapper && only !== null) {
            item.replaceWith(...Array.from(only.children));
        }
    });

    holder.querySelectorAll('span.bsee-mention').forEach(chip => {
        const marker = chip.getAttribute('data-double') === '1' ? '@@' : '@';
        const name = (chip.textContent ?? '').replace(/^@+/, '');
        chip.setAttribute('data-type', 'mention');
        chip.setAttribute('data-id', name);
        chip.setAttribute('data-label', name);
        chip.textContent = marker + name;
    });

    return holder.innerHTML;
};

/**
 * Replace what the editor shows (a document loaded by script, a revision picked from a list).
 * Writing the textarea alone isn't enough once the editor is attached; this updates both, and
 * doesn't count as a user edit (no input event, so no autosave).
 */
export const setEditorMarkdown = (field: HTMLTextAreaElement, markdown: string, serverHtml?: string): void => {
    field.value = markdown;
    const editor = editors.get(field);
    if (editor === undefined) {
        return;
    }
    if (serverHtml === undefined || serverHtml === '') {
        editor.commands.setContent(markdown, { contentType: 'markdown', emitUpdate: false });
        return;
    }
    editor.commands.setContent(htmlForEditor(serverHtml), { contentType: 'html', emitUpdate: false });
};

const attachAll = (root: ParentNode): void => {
    root.querySelectorAll<HTMLTextAreaElement>(SELECTOR).forEach(attach);
};

let started = false;

export const initBseeEditor = (): void => {
    if (started) {
        return;
    }
    started = true;

    attachAll(document);

    new MutationObserver(() => attachAll(document)).observe(document.body, { childList: true, subtree: true });
};

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initBseeEditor());
    } else {
        initBseeEditor();
    }
}
